import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import {
  alpha,
  Typography,
  Box,
  Container,
  Paper,
  IconButton,
  Button,
  Grid,
  Chip,
  Tooltip,
  CircularProgress
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SearchBar from "components/SearchBar/SearchBar";
import FilterSidebar from "components/FilterSidebar/FilterSidebar";
import { fetchDataRequest } from "../../redux/api/apiSlice";
import { searchQueryChanged, setSelectedFilters, resetFiltersAndSearch } from "../../redux/searchAndFilter/searchAndFilterSlice";
import styles from "./Questionneaire.module.css";
import Pagination from "components/Pagination/Pagination";
import { ArrowBack } from "@mui/icons-material";
import default_theme from "assets/themes/themes";

const DATASET_NAME = "RESILIAGE T1.2 Solutions Catalog";
const searchDelay = 700;
const cardsPerPage = 12;

const Questionnaire = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasets, pagination, loading } = useSelector((state) => state.api);
  const { searchQuery, selectedFilters: reduxSelectedFilters } = useSelector((state) => state.searchFilter);
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const questionnaire = datasets?.[DATASET_NAME] || [];
  const [currentPage, setCurrentPage] = useState(location.state?.currentPage || 1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  
  // Get filters for this dataset, memoized to prevent infinite loops
  const selectedFilters = useMemo(() => {
    return reduxSelectedFilters[DATASET_NAME] || {};
  }, [reduxSelectedFilters]);

  useEffect(() => {
    const saved = localStorage.getItem("solutionsFavorites");
    if (saved) setFavorites(new Set(JSON.parse(saved)));
  }, []);

  // Initialize Redux state from location state if present (only once on mount)
  useEffect(() => {
    if (location.state?.searchQuery) {
      dispatch(searchQueryChanged(location.state.searchQuery));
    }
    if (location.state?.selectedFilters) {
      // Restore filters to Redux state
      Object.keys(location.state.selectedFilters).forEach(category => {
        location.state.selectedFilters[category].forEach(value => {
          dispatch(setSelectedFilters({ category, value, dataset: DATASET_NAME }));
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search query for API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => searchQuery);
  
  useEffect(() => {
    const handler = debounce((value) => {
      setDebouncedSearchQuery(value);
    }, searchDelay);
    handler(searchQuery);
    return () => handler.cancel();
  }, [searchQuery]);

  // Create a stable string representation of filters for comparison
  const filtersKey = useMemo(() => {
    return JSON.stringify(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    if (!showFavorites) {
      dispatch(fetchDataRequest({
        datasetName: DATASET_NAME,
        searchQuery: debouncedSearchQuery,
        filters: selectedFilters,
        page: currentPage,
        pageSize: cardsPerPage
      }));
    }
  }, [dispatch, debouncedSearchQuery, filtersKey, currentPage, showFavorites]);

  const paginationInfo = pagination?.[DATASET_NAME] || {};
  const totalPages = paginationInfo.totalPages || 1;
  const totalCount = paginationInfo.total || 0;

  const displayedEntries = useMemo(() => {
    if (showFavorites) {
      return questionnaire.filter((e) => favorites.has(e.entryId));
    }
    return questionnaire;
  }, [questionnaire, showFavorites, favorites]);

  const favoritesTotalPages = showFavorites 
    ? Math.max(1, Math.ceil(displayedEntries.length / cardsPerPage))
    : totalPages;

  const paginatedEntries = showFavorites
    ? displayedEntries.slice((currentPage - 1) * cardsPerPage, currentPage * cardsPerPage)
    : displayedEntries;

  const handleSearchChange = (e) => {
    dispatch(searchQueryChanged(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterChange = (category, value) => {
    dispatch(setSelectedFilters({ category, value, dataset: DATASET_NAME }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    dispatch(resetFiltersAndSearch(DATASET_NAME));
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () =>
    Object.values(selectedFilters).reduce((acc, vals) => acc + (vals ? vals.length : 0), 0);

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleEntryClick = (entry) => {
    // Save current state before navigating
    navigate(`/solutions/rural-development/solutions-catalog/${entry.entryId}`, {
      state: {
        fromCatalogList: true,
        searchQuery: searchQuery,
        selectedFilters: selectedFilters,
        currentPage: currentPage
      }
    });
  };

  const toggleFavorite = (entryId, e) => {
    e?.stopPropagation();
    const next = new Set(favorites);
    next.has(entryId) ? next.delete(entryId) : next.add(entryId);
    setFavorites(next);
    localStorage.setItem("solutionsFavorites", JSON.stringify([...next]));
  };

  const handleBackClick = () => {
    if (showFavorites) {
      setShowFavorites(false);
      setCurrentPage(1);
    } else {
      navigate('/solutions/rural-development');
    }
  };

  const activeCount = getActiveFiltersCount();
  const displayCount = showFavorites ? displayedEntries.length : totalCount;

  return (
    <Box className={styles.pageWrapper}
      sx={{
        flex: 1,
        backgroundColor: default_theme.palette.primary.white,
        minHeight: '100%',
        padding: 0,
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
            minHeight: "60px",
          }}
        >
          <button className={styles.backButton} onClick={handleBackClick}>
            <ArrowBack />
            {showFavorites ? "Back to Catalogue" : "Back to Solutions"}
          </button>
          <Typography variant="h4" sx={{ 
            mb: { xs: 1, sm: 0 },
            flexGrow: { xs: 1, sm: 0 }, 
            minWidth: "fit-content",
          }}>
            {showFavorites ? "Favorite Catalogs" : "Solutions Catalog"}
          </Typography>

          <Box sx={{ 
            display: "flex", 
            gap: 1, 
            alignItems: "center",
            flexShrink: 0,
          }}>
            {isAuthenticated && (
              <Button
                variant={showFavorites ? "contained" : "outlined"}
                startIcon={<FavoriteIcon />}
                onClick={() => { 
                  setShowFavorites((v) => !v); 
                  setCurrentPage(1); 
                }}
                size="small"
                sx={{
                  minWidth: "120px",
                  whiteSpace: "nowrap",
                  color: showFavorites ? default_theme.palette.primary.main : default_theme.palette.other.main,
                  backgroundColor: showFavorites ? default_theme.palette.other.main : "transparent",
                  borderColor: default_theme.palette.other.main,
                  "&:hover": {
                    backgroundColor: showFavorites ? default_theme.palette.other.tealDark : default_theme.palette.other.tealHover,
                    color: showFavorites ? default_theme.palette.primary.main : default_theme.palette.other.tealDark,
                  },
                }}
              >
                Favorites ({favorites.size})
              </Button>
            )}

            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => {
                setFiltersOpen((v) => !v);
              }}
              size="small"
              sx={{
                minWidth: "100px",
                whiteSpace: "nowrap",
                backgroundColor: filtersOpen ? default_theme.palette.other.main : "transparent",
                color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                borderColor: default_theme.palette.other.main,
                "&:hover": {
                  backgroundColor: filtersOpen ? default_theme.palette.other.main :  default_theme.palette.other.tealHover,
                  color: filtersOpen ? default_theme.palette.other.main : default_theme.palette.other.tealDark,
                  borderColor: default_theme.palette.other.tealDark,
                },
                "& .MuiButton-startIcon": {
                  color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                  marginRight: "8px",
                },
                "& .MuiSvgIcon-root": {
                  color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                },
                "&.MuiButton-outlined": {
                  color: default_theme.palette.other.main,
                  borderColor: default_theme.palette.other.main,
                },
                "&.MuiButton-contained": {
                  backgroundColor: default_theme.palette.other.main,
                  color: default_theme.palette.primary.main,
                },
              }}
            >
              Filters {activeCount > 0 ? `(${activeCount})` : ""}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <FilterSidebar
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            selectedFilters={selectedFilters}
            handleFilterChange={handleFilterChange}
            clearAllFilters={clearAllFilters}
            getActiveFiltersCount={getActiveFiltersCount}
          />
        </Box>

        <SearchBar searchQuery={searchQuery} onSearchChange={handleSearchChange} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {loading && !showFavorites ? (
              <Box display="inline-flex" alignItems="center">
                <CircularProgress size={16} thickness={4} color={default_theme.palette.primary.black}/> 
                <Box ml={1}>Loading...</Box> 
              </Box>
            ) : (
              <>
                {showFavorites ? "Favorite" : "Showing"} {displayCount} solution
                {displayCount !== 1 ? "s" : ""}
                {activeCount > 0 && !showFavorites &&
                  ` with ${activeCount} filter${activeCount !== 1 ? "s" : ""} applied`}
              </>
            )}
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {paginatedEntries.map((entry) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={entry.entryId}>
              <Paper
                sx={{
                  p: 3,
                  cursor: "pointer",
                  height: 280,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: alpha(default_theme.palette.primary.gray, '0.05'),
                    transform: "translateY(-4px)",
                    boxShadow: default_theme.palette.other.cardShadow,
                  },
                  transition: "all 0.3s ease-in-out",
                }}
                onClick={() => handleEntryClick(entry)}
              >
                {isAuthenticated && (
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: favorites.has(entry.entryId) ? "error.main" : "text.secondary",
                      "&:hover": { backgroundColor: default_theme.palette.other.hoverLight },
                    }}
                    onClick={(e) => toggleFavorite(entry.entryId, e)}
                  >
                    {favorites.has(entry.entryId) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  </IconButton>
                )}

                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    pr: 4,
                    fontSize: "1.125rem",
                    fontWeight: "bold",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {entry.combinedData.title_english}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flex: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.5,
                  }}
                >
                  {entry.combinedData.aims || "Click to view details..."}
                </Typography>

                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  {entry.combinedData.location_country && (
                    <Chip label={entry.combinedData.location_country} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {favoritesTotalPages > 1 && (
          <Pagination
            count={favoritesTotalPages}
            page={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Container>
    </Box>
  );
};

export default Questionnaire;